import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { adsSettings } from "../../data/dummyData";
import {
  BarChart4,
  DollarSign,
  PanelTop,
  Clock,
  RadioTower,
  Info,
  Smartphone,
  LayoutDashboard,
  Search,
  ShieldCheck,
  CheckCircle,
  XCircle,
  ExternalLink,
  Megaphone,
  PlusSquare,
} from "lucide-react";
import ActionButton from "./ActionButton";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAds } from "../../hooks/useAds";
import { toast } from "react-toastify";
import Skeleton from "../../components/ui/Skeleton";
import ToggleButton from "../../components/ui/ToggleButton";
import queryClient from "../../lib/queryClient";
import DeleteModal from "../../components/ui/DeleteModal";
import AdsManagementForm from "./AdsManagementForm";
import Pagination from "../../components/Pagination";

function AdsManagement() {
  const { logout } = useAuth();
  const [settings, setSettings] = useState({
    is_banner_ad_on: false,
    is_google_ad_on: false,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalLoading, setIsModalLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("");
  const [selectedAd, setSelectedAd] = useState(null);
  const [adList, setAdList] = useState([]);
  const [permission, setPermission] = useState({});
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: Math.ceil(adList.length / 10),
    showing: 10,
    total: 0,
  });
  const { createAds, deleteAds, fetchAds, toggleAdsConfiguration, updateAds } =
    useAds({
      filters: { search_value: searchQuery },
      pagination: { page: pageData.currentPage, limit: 10 },
      sort: { field: sortField, direction: sortDirection },
    });

  const adsQuery = useQuery({
    queryKey: [
      "ads",
      pageData.currentPage,
      searchQuery,
      sortField,
      sortDirection,
    ],
    queryFn: fetchAds,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!adsQuery.isLoading) {
      const response = adsQuery.data;
      setAdList(response?.data?.ads || []);
      setPageData(() => ({
        currentPage: response?.data?.pagination?.currentPage || 1,
        totalPages: response?.data?.pagination?.lastPage || 1,
        total: response?.data?.pagination?.total || 0,
        showing: response?.data?.pagination?.showing || 10,
      }));
      setSettings((prev) => ({
        ...prev,
        ...(response?.data?.ad_config || {}),
      }));
    }
  }, [adsQuery.data]);

  const openModal = (ad, mode) => {
    setSelectedAd(ad);
    setModalMode(mode);
    setShowModal(true);
    setIsModalLoading(true);
    setTimeout(() => setIsModalLoading(false), 1000);
  };

  const handleToggle = (field) => {
    let newGoogleAdOn = settings.is_google_ad_on;
    let newBannerAdOn = settings.is_banner_ad_on;

    if (field === "is_google_ad_on") {
      newGoogleAdOn = !settings.is_google_ad_on;
      if (newGoogleAdOn) {
        newBannerAdOn = false;
      } else {
        newBannerAdOn = true;
      }
    } else if (field === "is_banner_ad_on") {
      newBannerAdOn = !settings.is_banner_ad_on;
      if (newBannerAdOn) {
        newGoogleAdOn = false;
      } else {
        newGoogleAdOn = true;
      }
    }

    const body = {
      ad_configure_id: settings._id,
      is_google_ad_on: newGoogleAdOn,
      is_banner_ad_on: newBannerAdOn,
    };

    mutation.toggleAdsConfiguration.mutate(body, {
      onSuccess: () => {
        toast.success("Configuration Updated");
        queryClient.invalidateQueries("ads");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAd(null);
  };

  const mutation = {
    createAds: useMutation({
      mutationFn:
        modalMode === "edit"
          ? async (body) => await updateAds(body)
          : async (body) => await createAds(body),
    }),
    deleteAds: useMutation({
      mutationFn: async (id) => await deleteAds(id),
    }),
    toggleAdsConfiguration: useMutation({
      mutationFn: async (settings) => await toggleAdsConfiguration(settings),
    }),
  };

  const handleDelete = {
    onDelete: (ad) =>
      mutation.deleteAds.mutate(ad._id, {
        onSuccess: async (data) => {
          toast.success(data.message || "Ads Deleted");
          setDeleteModal(false);
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (ad) => {
      setDeleteModal(true);
      setSelectedAd(ad);
    },
  };

  const handleSubmit = (data) => {
    if (modalMode === "edit" && selectedAd?._id) {
      mutation.createAds.mutate(
        { ad_id: selectedAd._id, ...data },
        {
          onSuccess: async (data) => {
            toast.success(data.message || "Ads Updated");
            closeModal();
          },
          onError: (error) => {
            toast.error(error.message);
          },
        }
      );
    } else {
      mutation.createAds.mutate(data, {
        onSuccess: async (data) => {
          toast.success(data.message || "Ads Created");
          closeModal();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    }
  };

  return (
    <div>
      <main>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Ads Management</h1>
          <p className="text-gray-600">Configure ad settings for free ads</p>
        </div>

        {/* Ad Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-medium mb-4">Ad Configuration</h3>
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4 bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="mr-2">Google Ads</span>
                  <div className="relative group">
                    <Info className="h-4 w-4 text-gray-400 cursor-pointer" />
                    <div className="absolute bottom-full left-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      Google's advertising platform for displaying ads in the
                      app.
                    </div>
                  </div>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.is_google_ad_on}
                    onChange={() => handleToggle("is_google_ad_on")}
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="mr-2">Banner Ads</span>
                  <div className="relative group">
                    <Info className="h-4 w-4 text-gray-400 cursor-pointer" />
                    <div className="absolute bottom-full left-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      Static or animated banner ads displayed at the top or
                      bottom of the screen.
                    </div>
                  </div>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.is_banner_ad_on}
                    onChange={() => handleToggle("is_banner_ad_on")}
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>
          {/* Google Account Authentication Section */}
          <div>
            <h3 className="text-md font-medium mb-4">Google Account Console</h3>
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4 bg-gray-50 p-4 rounded-lg">
              <p className="mb-4 text-gray-600">
                Open Google Ad Console to view revenue and to manage Google Ads.
              </p>
              <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition">
                <a
                  href="https://admob.google.com/v2/home"
                  target="_blank"
                  className="flex items-center space-x-1"
                >
                  <span>Open Google AdMob Console</span>
                  <ExternalLink className="h-5 w-5" />
                </a>
              </button>
            </div>
          </div>
        </div>

        <div className="mb-6 mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Ad List</h1>
          <button
            onClick={() => openModal({}, "create")}
            className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusSquare className="mr-2 h-4 w-4" />
            Add New Ad
          </button>
        </div>

        {/* Ad List Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 overflow-hidden">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search Ads..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          {adsQuery.isLoading ? (
            <Skeleton.Table columns={5} rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Title
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Description
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Loyalty Points
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Status
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adList.length > 0 ? (
                    adList.map((ad) => (
                      <tr key={ad._id} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-3 justify-items-start">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-primary-50 rounded-full flex items-center justify-center">
                              <Megaphone className="h-4 w-4 text-primary" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">
                                {ad.title}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 justify-items-start">
                          {ad.description}
                        </td>
                        <td className="px-4 py-3 text-sm justify-items-start">
                          {ad.loyalty_points}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 justify-items-start">
                          {mutation.createAds.isPending &&
                          modalMode === "edit" ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                ad.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                setModalMode("edit");
                                handleSubmit({
                                  ad_id: ad._id,
                                  is_active: !ad.is_active,
                                });
                              }}
                              title="Toggle Status"
                            >
                              {ad.is_active ? (
                                <CheckCircle className="mr-1 h-3 w-3" />
                              ) : (
                                <XCircle className="mr-1 h-3 w-3" />
                              )}
                              {ad.is_active ? "Active" : "Inactive"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 justify-items-start">
                          <ActionButton
                            item={ad}
                            openModal={openModal}
                            key={ad._id}
                            handleDelete={handleDelete}
                            setPermission={setPermission}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No ads found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>

        {/* Notes card */}
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-800">Important Notes</h3>
              <ul className="mt-2 text-sm text-yellow-700 space-y-1 list-disc list-inside">
                <li>
                  Ads will only be shown to free ads, not premium subscribers
                </li>
                <li>Higher ad frequency may lead to lower ad retention</li>
                <li>Ad revenue estimates are based on current ad activity</li>
                <li>
                  Ad network configurations may take up to 24 hours to fully
                  apply
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedAd)}
        title={`Delete ${selectedAd?.title || ""}`}
      />
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "User Details"
                  : modalMode === "edit"
                  ? "Edit User"
                  : "Create User"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <AdsManagementForm
                mode={modalMode}
                onCancel={closeModal}
                onSubmit={handleSubmit}
                defaultValues={selectedAd}
                submissionLoading={mutation.createAds.isPending}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdsManagement;
